import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Bell, ChevronRight, LogOut, Menu, Moon, Search, Sun } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";
import { useAppMode } from "@/hooks/use-app-mode";
import { ROLE_LABELS } from "@/lib/format";
import { cn } from "@/lib/utils";
import { readNavOpen, saveNavOpen } from "@/lib/navState";
import { NAV_SECTIONS } from "@/components/layout/nav";
import { MobileSidebar } from "@/components/layout/MobileSidebar";
import { ScreenPops } from "@/components/phone/ScreenPops";
import { AutoReload } from "@/components/layout/AutoReload";
import { SupportBanner } from "@/components/layout/SupportBanner";
import { ForcePasswordChange } from "@/components/auth/ForcePasswordChange";
import { ForceMfaEnrol } from "@/components/auth/ForceMfaEnrol";

const useUnread = () => {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/notifications/unread-count").then((r) => alive && setCount(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("cts:notifications-changed", load);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener("cts:notifications-changed", load);
    };
  }, [pathname]);
  return count;
};

// [endpoint, response field, refresh on ticket changes, tone]
const BADGES = {
  attention: ["/tickets-attention/count", "count", false, "bg-destructive text-destructive-foreground"],
  unlogged: ["/phone/unlogged/count", "count", false, "bg-destructive text-destructive-foreground"],
  unassigned: ["/tickets/counts", "unassigned", true, "bg-black text-white dark:bg-white dark:text-black"],
  needs_brand: ["/tickets/counts", "needs_brand", true, "bg-black text-white dark:bg-white dark:text-black"],
};
const NavBadge = ({ kind }) => {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  const [path, field, onChange, tone] = BADGES[kind];
  useEffect(() => {
    let alive = true;
    const load = () => api.get(path).then((r) => alive && setN(r.data[field])).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    if (onChange) window.addEventListener("cts:tickets-changed", load);
    return () => { alive = false; clearInterval(id); window.removeEventListener("cts:tickets-changed", load); };
  }, [path, field, onChange, pathname]);
  return n > 0 ? <span className={cn("ml-auto min-w-5 h-5 px-1.5 rounded-sm text-[11px] font-bold tabular-nums flex items-center justify-center", tone)} data-testid={`nav-${kind}-count`}>{n > 99 ? "99+" : n}</span> : null;
};

const BellButton = ({ count, onNavigate }) => (
  <Button variant="ghost" size="icon" className="relative h-8 w-8" asChild data-testid="notification-bell">
    <Link to="/notifications" onClick={onNavigate} title="Notifications">
      <Bell className="h-4 w-4 text-muted-foreground" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-count">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  </Button>
);

export { useAppMode };

const NavSection = ({ section, role, onNavigate }) => {
  const { pathname, search } = useLocation();
  const mode = useAppMode();
  const items = section.items.filter((i) => (!i.roles || i.roles.includes(role)) && !(i.previewOnly && mode !== "preview"));
  const isActive = (to) => (to === "/" ? pathname === "/" : to.includes("?") ? pathname + search === to || (pathname === "/tickets" && search.startsWith(to.split("/tickets")[1])) : pathname.startsWith(to));
  const containsActive = items.some((i) => isActive(i.to));
  const [open, setOpen] = useState(() => readNavOpen(section.id) || containsActive);
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);
  const toggle = () => setOpen((o) => { saveNavOpen(section.id, !o); return !o; });
  if (!items.length) return null;
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "flex w-full items-center gap-1 px-3 py-1 text-xs font-medium uppercase tracking-wider transition-colors hover:text-foreground",
          containsActive ? "text-primary" : "text-muted-foreground"
        )}
        data-testid={`nav-section-${section.id}`}
        aria-expanded={open}
      >
        <ChevronRight className={cn("h-3 w-3 transition-transform duration-200", open && "rotate-90")} />
        {section.label}
      </button>
      {open &&
        items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            className={() => cn("nav-item", isActive(item.to) && "active")}
            data-testid={`nav-${item.id}`}
          >
            {() => (
              <>
                <item.icon className={cn("h-4 w-4", isActive(item.to) ? "text-foreground" : "text-muted-foreground")} />
                <span className="truncate">{item.label}</span>
                {item.badge && <NavBadge kind={item.badge} />}
              </>
            )}
          </NavLink>
        ))}
    </div>
  );
};

const GlobalSearch = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <></>
  );
};

export const SidebarContent = ({ onNavigate, mobile = false }) => {
  const sfx = mobile ? "-mobile" : "";
  const { user, logout } = useAuth();
  const { styling } = useBranding();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unread = useUnread();
  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex h-14 items-center justify-between gap-2 border-b px-4">
        <Link to="/" onClick={onNavigate} className="font-bold text-primary truncate" data-testid="sidebar-title">
          {styling?.sidebar_title || "Ticket System"}
        </Link>
        <BellButton count={unread} onNavigate={onNavigate} />
      </div>
      <GlobalSearch onNavigate={onNavigate} />
      <nav className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="sidebar-nav">
        {NAV_SECTIONS.map((s) => (
          <NavSection key={s.id} section={s} role={user?.role} onNavigate={onNavigate} />
        ))}
      </nav>
      <div className="mt-auto border-t p-3 space-y-2">
        <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild>
          <Link to="/profile" onClick={onNavigate} aria-label="My profile" className="group flex items-center gap-3 rounded-sm px-2 py-1.5 cursor-pointer hover:bg-muted focus-visible:bg-muted transition-colors" data-testid={`sidebar-user${sfx}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full avatar bg-primary/20 text-xs font-bold text-foreground">{user?.initials || initials}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium group-hover:underline underline-offset-2">{user?.name}</span>
              <span className="block truncate text-xs text-muted-foreground" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role]}</span>
            </span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        </TooltipTrigger><TooltipContent side="top" data-testid={`sidebar-user-tooltip${sfx}`}>My profile</TooltipContent></Tooltip></TooltipProvider>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="flex-1 justify-start" onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")} data-testid={`theme-toggle${sfx}`}>
            {resolvedTheme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Sign out"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid={`logout-button${sfx}`}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AppShell = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { styling } = useBranding();
  const { user } = useAuth();
  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden lg:flex w-56 shrink-0 border-r" data-testid="sidebar">
        <div className="w-full">
          <SidebarContent />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        {user?.role === "admin" && <SupportBanner />}
        <header className="glass-header flex h-14 shrink-0 items-center gap-3 px-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} title="Open menu" data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-bold text-primary truncate">{styling?.sidebar_title || "Ticket System"}</span>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 bg-[#F8F9FA] dark:bg-background" data-testid="main-content">
          {user?.must_change_password ? <ForcePasswordChange /> : user?.mfa_enrolment_required ? <ForceMfaEnrol /> : <Outlet />}
        </main>
      </div>
      <MobileSidebar open={mobileOpen} onOpenChange={setMobileOpen} />
      <ScreenPops />
      <AutoReload />
    </div>
  );
};
